import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { getMediaUrl } from "../../services/media.ts";

const PREVIEWABLE_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg']);

const PatientDataTable = ({ dataItems, onRemoveItem, onUploadAnnotation, onUploadMap }) => {
    const [filterType, setFilterType] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [previewItem, setPreviewItem] = useState(null);
    const [imgError, setImgError] = useState(false);
    const [dragPos, setDragPos] = useState({ x: 120, y: 120 });
    const dragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    const isPreviewable = (item) =>
        PREVIEWABLE_EXT.has((item.original_file_type || '').toLowerCase());

    const getPreviewUrl = (item) => {
        const raw = item.file || item.file_url || '';
        return raw ? getMediaUrl(raw) : '';
    };

    const handleDragStart = useCallback((e) => {
        dragging.current = true;
        dragOffset.current = { x: e.clientX - dragPos.x, y: e.clientY - dragPos.y };
        e.preventDefault();
    }, [dragPos]);

    useEffect(() => {
        const onMove = (e) => {
            if (!dragging.current) return;
            setDragPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
        };
        const onUp = () => { dragging.current = false; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, []);

    const openPreview = (item) => {
        setImgError(false);
        setPreviewItem(item);
        setDragPos({ x: Math.max(20, window.innerWidth / 2 - 200), y: 100 });
    };

    const dataTypeLabels = {
        imaging: "Medical Imaging",
        image: "Images",
        document: "Documents",
        table: "Tables",
        signal: "Signals",
        pathology: "Pathology"
    };

    const dataTypeColors = {
        imaging: "primary",
        image: "success",
        document: "warning",
        table: "info",
        signal: "secondary",
        pathology: "danger"
    };

    const dataTypeIcons = {
        imaging: "fa-x-ray",
        image: "fa-image",
        document: "fa-file-text",
        table: "fa-table",
        signal: "fa-wave-square",
        pathology: "fa-microscope"
    };

    const getFileIcon = (fileType) => {
        const iconMap = {
            dcm: 'fa-file-medical',
            nii: 'fa-brain',
            'nii.gz': 'fa-brain',
            jpg: 'fa-file-image',
            jpeg: 'fa-file-image',
            png: 'fa-file-image',
            gif: 'fa-file-image',
            bmp: 'fa-file-image',
            tiff: 'fa-file-image',
            pdf: 'fa-file-pdf',
            doc: 'fa-file-word',
            docx: 'fa-file-word',
            txt: 'fa-file-text',
            md: 'fa-file-code',
            xlsx: 'fa-file-excel',
            xls: 'fa-file-excel',
            csv: 'fa-file-csv',
            edf: 'fa-wave-square',
            svs: 'fa-microscope',
            ndpi: 'fa-microscope',
        };
        return iconMap[fileType] || 'fa-file';
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString();
    };

    const filteredItems = dataItems.filter(item => {
        if (filterType === "annotations") return item.is_annotation || item.is_map;
        if (filterType !== "all" && item.data_type !== filterType) return false;
        if (filterType !== "all" && filterType !== "annotations") {
            if (item.is_annotation || item.is_map) return false;
        }
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            return (
                item.filename?.toLowerCase().includes(search) ||
                item.description?.toLowerCase().includes(search) ||
                item.original_file_type?.toLowerCase().includes(search)
            );
        }
        return true;
    });

    const getTypeCounts = () => {
        const counts = { all: dataItems.length };
        dataItems.forEach(item => {
            if (item.is_annotation || item.is_map) {
                counts.annotations = (counts.annotations || 0) + 1;
            } else {
                counts[item.data_type] = (counts[item.data_type] || 0) + 1;
            }
        });
        return counts;
    };

    const typeCounts = getTypeCounts();

    const getParentFilename = (item) => {
        if (!item.metadata?.parent_data_id) return null;
        const parent = dataItems.find(d => d.id === item.metadata.parent_data_id);
        return parent?.filename || item.metadata?.parent_data_filename || null;
    };

    return (
        <div className="patient-data-table-container">
            {/* Filter Tabs */}
            <div className="data-filter-tabs">
                <button
                    className={`filter-tab ${filterType === "all" ? "active" : ""}`}
                    onClick={() => setFilterType("all")}
                >
                    All Data
                    <span className="tab-count">{typeCounts.all}</span>
                </button>
                {Object.keys(dataTypeLabels).map(type => (
                    typeCounts[type] > 0 && (
                        <button
                            key={type}
                            className={`filter-tab ${filterType === type ? "active" : ""}`}
                            onClick={() => setFilterType(type)}
                        >
                            <i className={`fa ${dataTypeIcons[type]} me-1`}></i>
                            {dataTypeLabels[type]}
                            <span className="tab-count">{typeCounts[type]}</span>
                        </button>
                    )
                ))}
                {typeCounts.annotations > 0 && (
                    <button
                        className={`filter-tab ${filterType === "annotations" ? "active" : ""}`}
                        onClick={() => setFilterType("annotations")}
                    >
                        <i className="fa fa-layer-group me-1"></i>
                        Annotations / Maps
                        <span className="tab-count">{typeCounts.annotations}</span>
                    </button>
                )}
            </div>

            {/* Search */}
            <div className="data-search">
                <div style={{ position: "relative", maxWidth: "260px" }}>
                    <i className="fa fa-search" style={{ position: "absolute", left: "9px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)", fontSize: "11px", pointerEvents: "none" }} />
                    <input
                        type="text"
                        placeholder="Search files..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: "100%", padding: "5px 10px 5px 28px",
                            background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "6px", color: "rgba(255,255,255,0.8)", fontSize: "12px",
                            fontFamily: "Poppins, sans-serif", outline: "none",
                        }}
                    />
                </div>
            </div>

            {/* Table */}
            <table className="patients-table data-items-table">
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Filename</th>
                        <th>Format</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th>Uploaded</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredItems.map((item) => {
                        const parentFilename = getParentFilename(item);
                        return (
                            <tr key={item.id}>
                                <td>
                                    <span className={`badge bg-${dataTypeColors[item.data_type] || 'secondary'}`}>
                                        <i className={`fa ${dataTypeIcons[item.data_type] || 'fa-file'} me-1`}></i>
                                        {dataTypeLabels[item.data_type] || item.data_type}
                                    </span>
                                </td>
                                <td>
                                    <div className="file-name-cell">
                                        {/* Clickable icon for previewable types */}
                                        {isPreviewable(item) ? (
                                            <i
                                                className={`fa ${getFileIcon(item.original_file_type)} file-type-icon me-2`}
                                                onClick={() => openPreview(item)}
                                                title="Click to preview image"
                                                style={{ cursor: 'pointer', color: '#38b6ff', transition: 'opacity 0.15s' }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                                                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                            ></i>
                                        ) : (
                                            <i className={`fa ${getFileIcon(item.original_file_type)} file-type-icon me-2`}></i>
                                        )}
                                        <div>
                                            <span className="file-name" title={item.filename} style={{ color: "rgba(255,255,255,0.88)" }}>
                                                {item.filename}
                                            </span>
                                            {parentFilename && (
                                                <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                                                    <i className="fa fa-link me-1"></i>
                                                    for: {parentFilename}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className="badge bg-light text-dark">
                                        {item.original_file_type?.toUpperCase()}
                                    </span>
                                </td>
                                <td>
                                    {item.is_annotation ? (
                                        <span className="badge bg-warning text-dark">
                                            <i className="fa fa-tag me-1"></i>
                                            Annotation
                                        </span>
                                    ) : item.is_map ? (
                                        <span className="badge bg-info text-white">
                                            <i className="fa fa-map me-1"></i>
                                            Map
                                        </span>
                                    ) : (
                                        <span className="badge bg-success">
                                            <i className="fa fa-database me-1"></i>
                                            Source Data
                                        </span>
                                    )}
                                </td>
                                <td>{item.description || '-'}</td>
                                <td>{formatDate(item.uploaded_at)}</td>
                                <td>
                                    <div className="d-flex gap-1">
                                        {isPreviewable(item) && (
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => openPreview(item)}
                                                title="Preview image"
                                            >
                                                <i className="fa fa-eye"></i>
                                            </button>
                                        )}
                                        {!item.is_annotation && !item.is_map && onUploadAnnotation && (
                                            <button
                                                className="btn btn-sm btn-outline-warning"
                                                onClick={() => onUploadAnnotation(item)}
                                                title="Upload annotation for this data"
                                            >
                                                <i className="fa fa-tag"></i>
                                            </button>
                                        )}
                                        {!item.is_annotation && !item.is_map && onUploadMap && (
                                            <button
                                                className="btn btn-sm btn-outline-info"
                                                onClick={() => onUploadMap(item)}
                                                title="Upload map for this data"
                                            >
                                                <i className="fa fa-map"></i>
                                            </button>
                                        )}
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => onRemoveItem(item.id)}
                                            title="Remove from patient"
                                        >
                                            <i className="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {filteredItems.length === 0 && (
                <div className="empty-state">
                    <i className="fa fa-search"></i>
                    <p>No data items found</p>
                    <small className="text-muted">Try adjusting your filters or search term</small>
                </div>
            )}

            {/* Draggable Image Preview — rendered via portal to escape any stacking context */}
            {previewItem && createPortal(
                <>
                    {/* Transparent backdrop: click anywhere outside to close */}
                    <div
                        onClick={() => setPreviewItem(null)}
                        style={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'transparent' }}
                    />
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: dragPos.x,
                            top: dragPos.y,
                            zIndex: 99999,
                            background: '#0d1020',
                            border: '1px solid rgba(255,255,255,0.18)',
                            borderRadius: '10px',
                            boxShadow: '0 16px 48px rgba(0,0,0,0.85)',
                            minWidth: '300px',
                            maxWidth: '620px',
                            userSelect: 'none',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Title bar / drag handle */}
                        <div
                            onMouseDown={handleDragStart}
                            style={{
                                cursor: 'grab',
                                padding: '10px 14px',
                                background: 'rgba(255,255,255,0.06)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                                gap: '10px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                <i className="fa fa-arrows" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', flexShrink: 0 }} />
                                <i className="fa fa-image" style={{ color: '#38b6ff', flexShrink: 0 }} />
                                <span style={{ color: '#e0e8f5', fontSize: '13px', fontFamily: 'Poppins, sans-serif', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {previewItem.filename}
                                </span>
                            </div>
                            {/* Close button — stopPropagation so drag doesn't start */}
                            <button
                                onMouseDown={e => e.stopPropagation()}
                                onClick={() => setPreviewItem(null)}
                                style={{
                                    background: 'rgba(239,68,68,0.75)',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    flexShrink: 0,
                                    width: '26px',
                                    height: '26px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,1)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.75)'}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Image */}
                        <div style={{ padding: '14px', background: '#080c1a', minHeight: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {imgError ? (
                                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontFamily: 'Poppins, sans-serif', fontSize: '13px', padding: '20px' }}>
                                    <i className="fa fa-exclamation-circle" style={{ fontSize: '28px', display: 'block', marginBottom: '8px' }} />
                                    Could not load image
                                </div>
                            ) : (
                                <img
                                    src={getPreviewUrl(previewItem)}
                                    alt={previewItem.filename}
                                    onError={() => setImgError(true)}
                                    style={{ maxWidth: '100%', maxHeight: '520px', borderRadius: '6px', display: 'block', margin: '0 auto' }}
                                />
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

export default PatientDataTable;
